'use client';

import { useEffect, useRef, useState } from 'react';

interface HUDProps {
  score: number;
  speedTier: number;
}

const SPEED_LABELS = ['', 'WARP 1', 'WARP 2', 'WARP 3', 'WARP 4', 'WARP 5', 'WARP 6', 'WARP 7', 'WARP 8', 'WARP 9', 'LIGHTSPEED'];
const SPEED_COLORS = [
  '', '#00e5ff', '#00e5ff', '#29b6f6', '#26c6da', '#66bb6a',
  '#ffca28', '#ffa726', '#ef5350', '#e53935', '#ff1744',
];

export default function HUD({ score, speedTier }: HUDProps) {
  const prevScoreRef = useRef(score);
  const [scoreFlash, setScoreFlash] = useState(false);

  useEffect(() => {
    if (score !== prevScoreRef.current) {
      prevScoreRef.current = score;
      setScoreFlash(true);
      const t = setTimeout(() => setScoreFlash(false), 200);
      return () => clearTimeout(t);
    }
  }, [score]);

  const speedColor = SPEED_COLORS[speedTier] || '#00e5ff';
  const speedLabel = SPEED_LABELS[speedTier] || 'WARP 1';

  return (
    <div className="absolute inset-0 pointer-events-none select-none">
      {/* Top-left: Score */}
      <div className="absolute top-5 left-6 flex flex-col gap-0.5">
        <span
          className="font-mono-game text-[10px] font-600 tracking-[0.2em] uppercase"
          style={{ color: 'rgba(0, 229, 255, 0.6)' }}
        >
          SCORE
        </span>
        <span
          className="font-mono-game text-3xl font-800 transition-all duration-100"
          style={{
            color: '#00e5ff',
            textShadow: scoreFlash
              ? '0 0 20px #00e5ff, 0 0 40px #00e5ff' :'0 0 8px rgba(0, 229, 255, 0.6)',
            transform: scoreFlash ? 'scale(1.05)' : 'scale(1)',
          }}
        >
          {score.toLocaleString('da-DK')}
        </span>
      </div>

      {/* Top-right: Speed tier */}
      <div className="absolute top-5 right-6 flex flex-col items-end gap-0.5">
        <span
          className="font-mono-game text-[10px] font-600 tracking-[0.2em] uppercase"
          style={{ color: 'rgba(0, 229, 255, 0.6)' }}
        >
          HASTIGHED
        </span>
        <span
          className="font-mono-game text-xl font-700"
          style={{
            color: speedColor,
            textShadow: `0 0 8px ${speedColor}`,
            transition: 'color 0.5s ease, text-shadow 0.5s ease',
          }}
        >
          {speedLabel}
        </span>
        {/* Speed bar */}
        <div className="flex gap-0.5 mt-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={`speed-bar-${i}`}
              className="w-3 h-2 rounded-sm transition-all duration-300"
              style={{
                background: i < speedTier
                  ? speedColor
                  : 'rgba(255,255,255,0.1)',
                boxShadow: i < speedTier ? `0 0 4px ${speedColor}` : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom center: Controls hint */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4"
        style={{ opacity: 0.45 }}
      >
        <div className="flex items-center gap-1.5">
          <kbd
            className="font-mono-game text-[10px] px-1.5 py-0.5 rounded"
            style={{
              background: 'rgba(0, 229, 255, 0.08)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              color: 'rgba(0, 229, 255, 0.8)',
            }}
          >
            ←
          </kbd>
          <kbd
            className="font-mono-game text-[10px] px-1.5 py-0.5 rounded"
            style={{
              background: 'rgba(0, 229, 255, 0.08)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              color: 'rgba(0, 229, 255, 0.8)',
            }}
          >
            →
          </kbd>
          <span
            className="font-mono-game text-[10px] tracking-wide"
            style={{ color: 'rgba(0, 229, 255, 0.6)' }}
          >
            STYR
          </span>
        </div>
        <span style={{ color: 'rgba(0, 229, 255, 0.3)' }}>·</span>
        <div className="flex items-center gap-1.5">
          <kbd
            className="font-mono-game text-[10px] px-1.5 py-0.5 rounded"
            style={{
              background: 'rgba(0, 229, 255, 0.08)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              color: 'rgba(0, 229, 255, 0.8)',
            }}
          >
            A
          </kbd>
          <kbd
            className="font-mono-game text-[10px] px-1.5 py-0.5 rounded"
            style={{
              background: 'rgba(0, 229, 255, 0.08)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              color: 'rgba(0, 229, 255, 0.8)',
            }}
          >
            D
          </kbd>
          <span
            className="font-mono-game text-[10px] tracking-wide"
            style={{ color: 'rgba(0, 229, 255, 0.6)' }}
          >
            ELLER
          </span>
        </div>
      </div>

      {/* Left edge: distance indicator */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
        <div
          className="w-0.5 rounded-full"
          style={{
            height: '120px',
            background: 'linear-gradient(to bottom, rgba(0, 229, 255, 0.6), rgba(0, 229, 255, 0.05))',
          }}
        />
        <span
          className="font-mono-game text-[9px] tracking-widest"
          style={{
            color: 'rgba(0, 229, 255, 0.4)',
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            marginTop: '4px',
          }}
        >
          DISTANSE
        </span>
      </div>
    </div>
  );
}